import { initializeApp } from "firebase/app";
import { Analytics, getAnalytics, logEvent, setAnalyticsCollectionEnabled } from "firebase/analytics";
import { TelegramWebApps as Telegram } from "telegram-webapps";
import { getFunctions, httpsCallable } from "firebase/functions";
import { TonConnectUI, THEME } from '@tonconnect/ui';

declare function gtag(...args: any[]): void;

interface MiniAppMessage {
  method: string;
  value?: any;
}

interface StartParams {
  page: string;
  gameId?: string;
  gameCode?: string;
  inviterId?: string;
  code?: string;
  uuid?: string;
  env?: "dev" | "prod";
  eventId?: string;
  branch_link_id?: string;
  branch_url?: string;
  "~campaign"?: string;
  "~channel"?: string;
  "~feature"?: string;
  "~tags"?: string[];
  "~stage"?: string;
}

interface ShareData {
  text: string;
  button: {
    text: string;
    url: string;
  };
}

const CURRENT_VERSION = '1.0.0'; // Update this when you release a new version

// At the top of the file, make environment accessible globally
let currentEnvironment: Environment = 'production';

// Initialize TonConnectUI
let tonConnectUI: TonConnectUI | null = null;

function checkVersionAndReload(iframe: HTMLIFrameElement) {
    // Clear any cached data
    localStorage.clear();
    sessionStorage.clear();
}// Import the functions you need from the SDKs you need

// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: "AIzaSyAPAEZnDgeL2Io6GTY5hrnn_ulUwZIPuSw",
  authDomain: "quiz-9f12e.firebaseapp.com",
  projectId: "quiz-9f12e",
  storageBucket: "quiz-9f12e.appspot.com",
  messagingSenderId: "141025089440",
  appId: "1:141025089440:web:0df40e98c37150c64f36c5",
  measurementId: "G-PHTY6ET9PY"
};

type Environment = 'production' | 'development';

const ENV_CONFIG = {
  production: {
    appUrl: 'https://quiz-9f12e-miniapp.web.app',
    analyticsId: 'G-PHTY6ET9PY'
  },
  development: {
    appUrl: 'https://quiz-9f12e-miniapp-dev.web.app',
    analyticsId: 'G-PHTY6ET9PY'
  }
} as const;

function getStartParams(tg: Telegram.WebApp): { params: Record<string, string>, environment: Environment, parsedParams?: StartParams } {
  const params: Record<string, string> = {};
  let environment: Environment = 'production';
  let parsedParams: StartParams | undefined;

  const initStartParam = tg.initDataUnsafe?.start_param;
  console.log('Telegram start parameter:', initStartParam);
  
  if (initStartParam) {
    try {
      const decodedPayload = decodeBase64Url(initStartParam);
      console.log('Decoded start parameter:', decodedPayload);
      parsedParams = JSON.parse(decodedPayload) as StartParams;
      
      // Extract Branch attribution params with type safety
      if (parsedParams["~campaign"]) params['campaign'] = parsedParams["~campaign"];
      if (parsedParams["~channel"]) params['channel'] = parsedParams["~channel"];
      if (parsedParams["~feature"]) params['feature'] = parsedParams["~feature"];
      if (parsedParams["~tags"]) params['tags'] = parsedParams["~tags"].join(',');
      if (parsedParams["~stage"]) params['stage'] = parsedParams["~stage"];
      if (parsedParams.branch_link_id) params['branch_link_id'] = 
        parsedParams.branch_link_id;
      
      // Set environment based on start params
      if (parsedParams.env === 'dev') {
        environment = 'development';
      }
    } catch (error) {
      console.error('Error processing start parameters:', error);
    }
  }

  return { params, environment, parsedParams };
}

// Add this function to handle text field focus properly
function handleTextFieldFocus() {
  const tg: Telegram.WebApp = (window as any).Telegram.WebApp;
  tg.expand(); // Ensure the WebApp is expanded when text field is focused
}

// Add this function at the top with other utility functions
async function logToCloudConsole(severity: 'ERROR' | 'WARNING' | 'INFO', message: string, error?: any) {
  try {
    const logData = {
      severity,
      message,
      error: error ? JSON.stringify(error, Object.getOwnPropertyNames(error)) : undefined,
      platform: 'web',
      is_telegram: true,
      timestamp: new Date().toISOString(),
      environment: currentEnvironment,
    };

    // Use no-cors mode to avoid CORS issues
    await fetch('/api/log', {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: logData }),
    });
  } catch (err) {
    // Just log to console, don't try to send failed log attempts
    console.error('Failed to send log to Cloud Console:', err);
  }
}

// Update handleIframeError
function handleIframeError(iframe: HTMLIFrameElement, analytics: Analytics) {
  iframe.onerror = (event) => {
    console.error('Iframe error:', event);
    
    // Log to Cloud Console
    logToCloudConsole('ERROR', 'Iframe failed to load', event);
    
    // Log to Analytics
    logEvent(analytics, 'iframe_error', {
      error: 'Iframe failed to load',
      platform: 'web',
      is_telegram: 'true',
    });
  };

  iframe.onload = () => {
    // Don't try to access iframe content - it will always fail due to CORS
    // Instead, just verify the iframe src loaded
    if (iframe.src) {
      console.log('Iframe loaded successfully:', iframe.src);
    } else {
      const error = new Error('Iframe failed to load - no src');
      console.error('Iframe load error:', error);
      
      // Log to Cloud Console
      logToCloudConsole('ERROR', 'Iframe failed to load - no src', {
        message: error.message,
        stack: error.stack,
      });
      
      // Log to Analytics
      logEvent(analytics, 'iframe_error', {
        error: error.message,
        platform: 'web',
        is_telegram: 'true',
      });
    }
  };
}

// Update setupGlobalErrorHandling
function setupGlobalErrorHandling(analytics: Analytics) {
  window.onerror = (message, source, lineno, colno, error) => {
    console.error('Global error:', { message, source, lineno, colno, error });
    
    // Log to Cloud Console
    logToCloudConsole('ERROR', 'Global JavaScript error', {
      message,
      source,
      line: lineno,
      column: colno,
      stack: error?.stack,
    });
    
    // Log to Analytics
    logEvent(analytics, 'hosting_error', {
      error: message.toString(),
      source: source || 'unknown',
      line: lineno?.toString() || 'unknown',
      column: colno?.toString() || 'unknown',
      stack: error?.stack || 'no stack trace',
      platform: 'web',
      is_telegram: 'true',
    });
    return false;
  };

  window.onunhandledrejection = (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    
    // Log to Cloud Console
    logToCloudConsole('ERROR', 'Unhandled Promise Rejection', {
      reason: event.reason,
      stack: event.reason?.stack,
    });
    
    // Log to Analytics
    logEvent(analytics, 'hosting_promise_error', {
      error: event.reason?.toString() || 'Unknown promise error',
      stack: event.reason?.stack || 'no stack trace',
      platform: 'web',
      is_telegram: 'true',
    });
  };
}

document.addEventListener('DOMContentLoaded', async () => {
  console.log('DOMContentLoaded: Starting initialization');
  
  const tg: Telegram.WebApp = (window as any).Telegram.WebApp;
  console.log('Telegram WebApp version:', tg.version);
  
  const iframe = document.getElementById('quizApp') as HTMLIFrameElement | null;
  
  // Get parameters from start_param and store parsed params
  const { params, environment, parsedParams } = getStartParams(tg);
  currentEnvironment = environment; // Store the environment

  // Set iframe src based on environment
  if (iframe) {
    const baseUrl = ENV_CONFIG[environment].appUrl;
    console.log('Base URL for environment:', baseUrl);
    
    // let iframeSrc = updateQueryStringParameter(baseUrl, 'v', 
    //   new Date().getTime().toString());
    
    // // Add Branch attribution parameters to iframe URL
    // Object.entries(params).forEach(([key, value]) => {
    //   iframeSrc = updateQueryStringParameter(iframeSrc, key, value);
    //   console.log(`Adding parameter to iframe - ${key}: ${value}`);
    // });
    
    // console.log('Final iframe src with parameters:', iframeSrc);
    iframe.src = baseUrl;
  }

  // Update analytics configuration
  gtag('config', ENV_CONFIG[environment].analyticsId);

  // Wait for GA to load
  await new Promise(resolve => {
    if ((window as any).gtag) {
      console.log('GA already loaded');
      resolve(true);
    } else {
      console.log('Waiting for GA to load');
      window.addEventListener('gtag-loaded', () => {
        console.log('GA loaded via event');
        resolve(true);
      });
    }
  });

  // Initialize Firebase
  console.log('Initializing Firebase');
  const app = initializeApp(firebaseConfig);
  const analytics = getAnalytics(app);

  // Set up error handling
  setupGlobalErrorHandling(analytics);
  if (iframe) {
    handleIframeError(iframe, analytics);
  }

  // Configure Analytics to not use cookies or local storage
  setAnalyticsCollectionEnabled(analytics, true);
  gtag('config', firebaseConfig.measurementId, { 
    'send_page_view': true,
    'client_storage': 'none',
    'storage': 'none',
    'cookie_update': false,
    'cookie_write': false,
    'cookie_flags': 'SameSite=None;Secure'
  });

  if (!iframe) {
    console.error('Required elements not found');
    return;
  }

  // Set the viewport height and disable vertical swipes
  tg.disableVerticalSwipes();

  // Handle viewport changes
  tg.onEvent('viewportChanged', adjustIframeHeight);

  // Initial height adjustment
  adjustIframeHeight();

  // Generate auth token
  let authToken: string | null = null;
  try {
    const response = await fetch('/api/generateTelegramAuthToken', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        data: { telegramId: tg.initDataUnsafe!.user?.id.toString() }
      }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    const json = await response.json();
    authToken = json.result; // Cloud Functions v2 wraps the response in a 'result' field
    if (authToken) {
      console.log('Auth token generated successfully');
    } else {
      throw new Error('Token not received in response');
    }
  } catch (error) {
    console.error('Error generating auth token:', error);
  }

  if (!authToken) {
    console.error('Failed to generate auth token');
    // Handle the error appropriately (e.g., show an error message to the user)
    return;
  }

  // Use parsedStartParams instead of decodedStartParams
  if (authToken) {
    console.log('Auth token generated successfully');
    // Use parsedStartParams here when needed
  }

  // Update the message event listener error handling
  window.addEventListener('message', (event: MessageEvent) => {
    if (event.source !== iframe?.contentWindow) return;

    try {
      console.log('Received message:', event.data);
      
      const { method, value } = (event.data.miniapp || {}) as MiniAppMessage;
      console.log('Parsed message:', { method, value });

      // Add error event handling
      if (method === 'error') {
        // Log to Cloud Console
        logToCloudConsole('ERROR', 'Iframe Application Error', {
          error: value?.error,
          stack: value?.stack,
        });
        
        // Log to Analytics
        logEvent(analytics, 'iframe_app_error', {
          error: value?.error || 'Unknown error',
          stack: value?.stack || 'no stack trace',
          platform: 'web',
          is_telegram: 'true',
        });
        return;
      }

      switch (method) {
        case 'loading':
          if (value === 100) {
            tg.expand();
            adjustIframeHeight();
            // Just remove the spinner when loading is complete
            const spinner = document.querySelector('.spinner');
            if (spinner) {
              spinner.remove();
            }
          }
          break;

        case 'sendAnalyticNewSession':
          console.log('New analytics session requested');
          break;
        
        case 'sendAnalytics':
          console.log('Analytics event received:', value);
          if (!analytics) {
            console.error('Analytics not initialized');
            return;
          }
          if (!gtag) {
            console.error('gtag not initialized');
            return;
          }
          console.log('Analytics and gtag available, handling event');
          handleAnalytics(analytics, value);
          break;

        case 'getAppState':
          console.log('App state requested');
          sendAppState(iframe);
          break;

        case 'getUserProfile':
          console.log('User profile requested');
          sendUserProfile(iframe, tg, authToken!, parsedParams);
          break;
        
        case 'getTelegramAuthToken':
          console.log('Auth token requested');
          sendTelegramAuthToken(iframe, tg, authToken!);
          break;

        case 'forceReload':
          console.log('Force reload requested');
          checkVersionAndReload(iframe);
          break;

        case 'closeMiniApp':
          console.log('Close mini app requested');
          tg.close();
          break;

        case 'openTelegramLink':
          console.log('Opening Telegram link:', value);
          if (value && typeof value === 'object' && 'text' in value && 'button' in value) {
            const shareData = value as ShareData;
            tg.switchInlineQuery(JSON.stringify(shareData), ['users', 'groups', 'channels']);
          } else if (typeof value === 'string') {
            tg.openTelegramLink(value);
          } else {
            console.error('Invalid data provided for openTelegramLink');
          }
          break;
        
        case 'openExternalLink':
          console.log('Opening external URL:', value);
          if (typeof value === 'string') {
            tg.openLink(value, { try_instant_view: false });
          }
          break;

        case 'logBranchEvent':
          handleBranchEvent(iframe, value, parsedParams);
          break;

        case 'getBranchInfo':
          console.log('Branch info requested');
          const branchInfo = {
            branch_link_id: parsedParams?.branch_link_id || '',
            branch_url: parsedParams?.branch_url || '',
            campaign: parsedParams?.["~campaign"] || '',
            channel: parsedParams?.["~channel"] || '',
            feature: parsedParams?.["~feature"] || '',
            tags: parsedParams?.["~tags"] || [],
            stage: parsedParams?.["~stage"] || ''
          };
          iframe.contentWindow?.postMessage({
            miniapp: {
              method: 'branchInfo',
              value: branchInfo
            }
          }, '*');
          break;

        case 'requestPayment':
          console.log('Payment requested:', value);
          if (typeof value === 'string') {
            tg.openInvoice(value, (status) => {
              // Handle payment status
              if (status === 'paid') {
                iframe.contentWindow?.postMessage({
                  miniapp: {
                    method: 'paymentComplete',
                    value: {
                      status: 'paid',
                      itemId: new URL(value).searchParams.get('itemId'),
                      // Add any other payment details from the invoice
                    }
                  }
                }, '*');
              } else {
                iframe.contentWindow?.postMessage({
                  miniapp: {
                    method: 'paymentComplete',
                    value: {
                      status: 'failed',
                      error: 'Payment was not completed'
                    }
                  }
                }, '*');
              }
            });
          }
          break;

        case 'openNativeShare':
          console.log('Opening native share:', value);
          if(window.navigator.canShare()) {
            console.log('Native share supported, sharing');
            window.navigator.share(value).then(() => {
              console.log('Native share completed');
              sendShareComplete(iframe);
            }).catch((error) => {
              console.error('Native share error:', error);
              sendShareComplete(iframe);
            });
          } else {
            console.error('Native share not supported');
            sendShareComplete(iframe);
          }
          break;

        case 'shareMessage':
          console.log('Opening Telegram share:', value);
          shareImageWithMessage(tg, iframe, value);
          break;

        case 'tonConnectOpenModal':
          console.log('Opening TON Connect modal');
          handleTonConnectOpenModal(iframe);
          break;
        
        case 'tonConnectCloseModal':
          console.log('Closing TON Connect modal');
          if (tonConnectUI) {
            tonConnectUI.closeModal();
          }
          break;
        
        case 'tonConnectDisconnect':
          console.log('Disconnecting TON Connect wallet');
          if (tonConnectUI) {
            tonConnectUI.disconnect();
          }
          break;
        
        case 'tonConnectGetStatus':
          console.log('Getting TON Connect status');
          iframe.contentWindow?.postMessage({
            miniapp: {
              method: 'tonConnectStatusChanged',
              value: {
                connected: !!tonConnectUI?.connected,
                walletInfo: tonConnectUI?.connected ? {
                  address: tonConnectUI?.wallet?.account?.address,
                  chain: tonConnectUI?.wallet?.account?.chain,
                  walletName: tonConnectUI?.wallet?.device?.appName,
                  walletVersion: tonConnectUI?.wallet?.device?.appVersion,
                } : null
              }
            }
          }, '*');
          break;
        
        case 'tonConnectSendTransaction':
          console.log('Sending TON Connect transaction');
          if (tonConnectUI && tonConnectUI.connected && value) {
            sendTransaction(iframe, value);
          } else {
            console.error('Cannot send transaction: wallet not connected or invalid transaction data');
          }
          break;

        default:
          console.log('Unknown method received:', method);
          break;
      }
    } catch (err) {
      const error = err as Error;
      console.error('Error processing iframe message:', error);
      
      // Log to Cloud Console
      logToCloudConsole('ERROR', 'Error processing iframe message', {
        message: error.message,
        stack: error.stack,
      });
      
      // Log to Analytics
      logEvent(analytics, 'iframe_message_error', {
        error: error.message || error.toString(),
        platform: 'web',
        is_telegram: 'true',
      });
    }
  });

  // Handle Telegram WebApp events
  tg.onEvent('mainButtonClicked', () => {
    iframe.contentWindow?.postMessage({
      miniapp: {
        method: 'play'
      }
    }, '*');
  });

  // Show the back button
  tg.BackButton.show();

  // Handle back button press
  tg.BackButton.onClick(() => {
    iframe.contentWindow?.postMessage({
      miniapp: {
        method: 'backButtonPressed'
      }
    }, '*');
  });

  // Add focus event listeners to text fields
  document.querySelectorAll('input[type="text"], textarea').forEach(element => {
    element.addEventListener('focus', () => {
      handleTextFieldFocus();
    });
  });

  // Initialize TON Connect UI after iframe is loaded
  iframe.addEventListener('load', () => {
    console.log('iframe loaded, initializing TON Connect UI');
    initializeTonConnectUI(iframe);
  });
});

function handleAnalytics(analytics: Analytics, value: { [key: string]: any }): void {
  console.log('handleAnalytics', value);
  try {
    // Add debug_mode to specific events
    const eventProperties = {
      ...value.event_properties,
      // debug_mode: true
    };

    // Log the exact event being sent to Firebase
    console.log('Logging event to Firebase:', {
      name: value.name,
      properties: eventProperties
    });

    // Try both gtag and Firebase Analytics methods
    // gtag('event', value.name, eventProperties);
    logEvent(analytics, value.name, eventProperties);
  } catch (error) {
    console.error('Error logging event:', error);
  }
}

function sendAppState(iframe: HTMLIFrameElement): void {
  iframe.contentWindow?.postMessage({
    miniapp: {
      method: 'getAppState',
      value: true // Assuming the mini-app is always open
    }
  }, '*');
}

function filterNullValues<T extends Record<string, any>>(
  obj: T
): Partial<T> {
  return (Object.entries(obj) as [keyof T, T[keyof T]][]).reduce(
    (acc: Partial<T>, [key, value]) => {
      if (value != null && value !== "") {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );
}

function getUserProfile(tg: Telegram.WebApp, authToken: string, startParams: StartParams | undefined) {
  return filterNullValues({
    avatar: tg.initDataUnsafe!.user?.photo_url,
    username: tg.initDataUnsafe!.user?.username,
    firstName: tg.initDataUnsafe!.user?.first_name,
    lastName: tg.initDataUnsafe!.user?.last_name,
    telegramId: tg.initDataUnsafe!.user?.id,
    locale: tg.initDataUnsafe!.user?.language_code,
    token: authToken,
    env: startParams?.env,
    // Nest routing parameters under "params" and filter nulls
    params: filterNullValues({
      page: startParams?.page,
      gameId: startParams?.gameId,
      gameCode: startParams?.gameCode,
      inviterId: startParams?.inviterId,
      code: startParams?.code,
      uuid: startParams?.uuid,
      eventId: startParams?.eventId,
    }),
  });
}

function sendUserProfile(iframe: HTMLIFrameElement, tg: Telegram.WebApp, authToken: string, startParams: StartParams | undefined): void {
  iframe.contentWindow?.postMessage({
    miniapp: {
      method: 'getUserProfile',
      value: getUserProfile(tg, authToken, startParams)
    }
  }, '*');
}

function sendTelegramAuthToken(iframe: HTMLIFrameElement, tg: Telegram.WebApp, authToken: string | null): void {
  iframe.contentWindow?.postMessage({
    miniapp: {
      method: 'getTelegramAuthToken',
      value: authToken
    }
  }, '*');
}

function adjustIframeHeight() {
  const tg: Telegram.WebApp = (window as any).Telegram.WebApp;
  const iframe = document.getElementById('quizApp') as HTMLIFrameElement | null;
  const loadingSplash = document.getElementById('loadingSplash');
  
  if (!iframe || !loadingSplash) {
    console.error('Required elements not found in adjustIframeHeight');
    return;
  }
  
  // Only request fullscreen in development environment and if version supports it
  // if (currentEnvironment === 'development') {
  //   const version = parseFloat(tg.version);
  //   if (version >= 8.0) {
  //     try {
  //       tg.requestFullscreen();
  //     } catch (error) {
  //       console.error('Error requesting fullscreen:', error);
  //     }
  //   } else {
  //     console.log(
  //       `Fullscreen not supported in WebApp version ${tg.version}. ` + 
  //       `Requires version 8.0 or higher.`
  //     );
  //   }
  // }
  
  const viewportHeight = tg.viewportStableHeight || tg.viewportHeight;
  iframe.style.height = `${viewportHeight}px`;
  loadingSplash.style.height = `${viewportHeight}px`;
  
  // Notify the iframe about the new height
  iframe.contentWindow?.postMessage({
    miniapp: {
      method: 'viewportChanged',
      value: {
        height: viewportHeight
      }
    }
  }, '*');
}

// Helper function to decode base64
function decodeBase64Url(str: string): string {
  // Replace non-url compatible chars with base64 standard chars
  str = str.replace(/-/g, '+').replace(/_/g, '/');
  // Pad out with standard base64 required padding characters
  const pad = str.length % 4;
  if (pad) {
    if (pad === 1) {
      throw new Error('InvalidLengthError: Input base64url string is the wrong length to determine padding');
    }
    str += new Array(5-pad).join('=');
  }
  return atob(str);
}

// Add this new function at the end of the file
function updateQueryStringParameter(uri: string, key: string, value: string) {
  const re = new RegExp("([?&])" + key + "=.*?(&|$)", "i");
  const separator = uri.indexOf('?') !== -1 ? "&" : "?";
  if (uri.match(re)) {
    return uri.replace(re, '$1' + key + "=" + value + '$2');
  }
  else {
    return uri + separator + key + "=" + value;
  }
}

function handleBranchEvent(
  iframe: HTMLIFrameElement, 
  value: any, 
  parsedStartParams?: StartParams
) {
  console.log('Branch event received:', value);
  if (parsedStartParams?.branch_link_id) {
    const branchData = {
      ...value,
      link_id: parsedStartParams.branch_link_id,
    };
    iframe.contentWindow?.postMessage({
      miniapp: {
        method: 'branchData',
        value: branchData
      }
    }, '*');
  }
}

// Add this helper function
function sendShareComplete(iframe: HTMLIFrameElement) {
  iframe.contentWindow?.postMessage({
    miniapp: {
      method: 'shareComplete'
    }
  }, '*');
}

async function shareImageWithMessage(
  tg: Telegram.WebApp,
  iframe: HTMLIFrameElement,
  value: { 
    imageUrl: string, 
    link: string, 
    title: string, 
    text: string,
    thumbnailUrl?: string // Add optional thumbnail
}): Promise<void> {
  try {
    console.log('Starting shareImageWithMessage with value:', value);
    
    if (!value || typeof value !== 'object') {
      console.error('Invalid value provided to shareImageWithMessage:', value);
      throw new Error('Invalid share data');
    }

    // Validate all required fields
    const requiredFields = ['imageUrl', 'link', 'title', 'text'];
    for (const field of requiredFields) {
      if (!value[field as keyof typeof value]) {
        console.error(`Missing required field: ${field}`);
        throw new Error(`Missing required field: ${field}`);
      }
    }

    // Check if shareMessage is supported (version 8.0+)
    if (tg.isVersionAtLeast('8.0')) {
      console.log('Using shareMessage (v8.0+)');
      
      // Validate we have the telegram user
      if (!tg.initDataUnsafe.user?.id) {
        console.error('No Telegram user ID available in initDataUnsafe:', tg.initDataUnsafe);
        throw new Error('No Telegram user ID available');
      }

      console.log('Preparing message payload for user:', tg.initDataUnsafe.user.id);
      const response = await fetch('/api/prepareTelegramMessage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          data: {
            photoUrl: value.imageUrl,
            thumbnailUrl: value.thumbnailUrl, // Add thumbnail URL if available
            title: value.title,
            description: value.text,
            caption: value.text,
            buttonUrl: value.link,
            userId: tg.initDataUnsafe.user.id
          }
        }),
      });

      if (!response.ok) {
        console.error('Failed to prepare message, status:', response.status);
        throw new Error('Failed to prepare message');
      }

      const responseData = await response.json();
      console.log('Received message preparation response:', responseData);
      
      // Extract messageId from the nested result structure
      const messageId = responseData.result?.messageId;
      if (!messageId) {
        console.error('No messageId in response:', responseData);
        throw new Error('Invalid response: no messageId');
      }
      
      console.log('Sharing message with ID:', messageId);
      tg.shareMessage(messageId, (success) => {
        console.log('Share message callback, success:', success);
        if (success) {
          sendShareComplete(iframe);
        } else {
          console.error('Share was not successful');
          sendShareComplete(iframe);
        }
      });
    } else {
      // Fallback for older versions: share to story
      console.log('Falling back to story sharing (version < 8.0)');
      tg.shareToStory(value.imageUrl, {
        text: value.link,
      });
      sendShareComplete(iframe);
    }
  } catch (error) {
    console.error('Error in shareImageWithMessage:', error);
    sendShareComplete(iframe);
  }
}

// Function to initialize TonConnectUI
function initializeTonConnectUI(iframe: HTMLIFrameElement) {
  try {
    console.log('Initializing TON Connect UI');
    
    // Create a container for the button if needed
    const buttonContainer = document.createElement('div');
    buttonContainer.id = 'ton-connect-button';
    buttonContainer.style.display = 'none'; // Hide it initially
    document.body.appendChild(buttonContainer);
    
    // Determine network based on environment
    const network = currentEnvironment === 'development' ? 'testnet' : 'mainnet';
    console.log(`Using TON network for connection: ${network}`);
    
    // Initialize TonConnectUI with iOS-specific configuration
    tonConnectUI = new TonConnectUI({
      manifestUrl: 'https://quiz-9f12e-telegram-miniapp.web.app/tonconnect-manifest.json',
      buttonRootId: 'ton-connect-button',
      uiPreferences: {
        theme: THEME.LIGHT,
        borderRadius: 's'
      },
      restoreConnection: true,
      // Add iOS-specific configuration
      actionsConfiguration: {
        modals: ['before', 'success', 'error'],
        notifications: ['before', 'success', 'error'],
        returnStrategy: 'back'
      }
    });
    
    // Set network after initialization
    tonConnectUI.setConnectRequestParameters({
      state: 'ready',
      value: { tonProof: network }
    });
    
    // Listen for wallet connection status changes
    tonConnectUI.onStatusChange(wallet => {
      console.log('TON Connect status changed:', wallet ? 'connected' : 'disconnected');
      
      // Notify Flutter app about connection status change
      iframe.contentWindow?.postMessage({
        miniapp: {
          method: 'tonConnectStatusChanged',
          value: {
            connected: !!wallet,
            walletInfo: wallet ? {
              address: wallet.account.address,
              chain: wallet.account.chain,
              walletName: wallet.device?.appName,
              walletVersion: wallet.device?.appVersion,
            } : null
          }
        }
      }, '*');
    });
    
    // Send initialization complete message to Flutter
    iframe.contentWindow?.postMessage({
      miniapp: {
        method: 'tonConnectInitialized',
        value: true
      }
    }, '*');
    
    console.log('TON Connect UI initialized successfully');
  } catch (error) {
    console.error('Error initializing TON Connect UI:', error);
    
    // Send initialization failed message to Flutter
    iframe.contentWindow?.postMessage({
      miniapp: {
        method: 'tonConnectInitialized',
        value: false
      }
    }, '*');
  }
}

// Add a helper function to detect iOS
function isIOS() {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
}

// Modify the openTonConnectModal method to handle iOS specifically
function handleTonConnectOpenModal(iframe: HTMLIFrameElement) {
  if (!tonConnectUI) {
    console.error('TON Connect UI not initialized');
    return;
  }
  
  // Determine network based on environment
  const network = currentEnvironment === 'development' ? 'testnet' : 'mainnet';
  console.log(`Using TON network for connection: ${network}`);
  
  // For iOS, we need to ensure the WebApp is expanded and focused
  const tg: Telegram.WebApp = (window as any).Telegram.WebApp;
  
  if (isIOS()) {
    console.log('Opening TON Connect modal on iOS');
    // Ensure WebApp is expanded
    tg.expand();
    
    // Disable Telegram's native gestures temporarily
    tg.disableClosingConfirmation();
    
    // Add a small delay before opening the modal on iOS
    setTimeout(() => {
      // Set the network before opening the modal
      tonConnectUI?.setConnectRequestParameters({
        state: 'ready',
        value: { tonProof: network }
      });
      tonConnectUI?.openModal();
      
      // Add event listener to prevent modal from being closed by background touches
      const modalOverlay = document.querySelector('.tc-modal-overlay');
      if (modalOverlay) {
        modalOverlay.addEventListener('touchstart', (e) => {
          // Only prevent default if the touch is on the overlay itself
          if (e.target === modalOverlay) {
            e.preventDefault();
            e.stopPropagation();
          }
        }, { passive: false });
      }
    }, 100);
  } else {
    // For non-iOS platforms, just open the modal directly
    console.log('Opening TON Connect modal on non-iOS platform');
    // Set the network before opening the modal
    tonConnectUI.setConnectRequestParameters({
      state: 'ready',
      value: { tonProof: network }
    });
    tonConnectUI.openModal();
  }
}

// Function to send a transaction
async function sendTransaction(iframe: HTMLIFrameElement, transactionData: any) {
  try {
    if (!tonConnectUI || !tonConnectUI.connected) {
      throw new Error('Wallet not connected');
    }
    
    console.log('Sending transaction:', transactionData);
    const result = await tonConnectUI.sendTransaction(transactionData);
    
    // Notify Flutter about transaction result
    iframe.contentWindow?.postMessage({
      miniapp: {
        method: 'tonConnectTransactionSent',
        value: {
          success: true,
          boc: result.boc
        }
      }
    }, '*');
  } catch (error) {
    console.error('Error sending transaction:', error);
    
    // Notify Flutter about transaction error
    iframe.contentWindow?.postMessage({
      miniapp: {
        method: 'tonConnectTransactionSent',
        value: {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      }
    }, '*');
  }
}
